import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { lazy, Suspense } from 'react';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

// Helper to wrap lazy components with Suspense
const Loadable = (Component: React.LazyExoticComponent<any> | React.ComponentType<any>) => (props: any) => (
    <Suspense fallback={<div className="flex h-screen w-full items-center justify-center"><LoadingSpinner size="lg" /></div>}>
        <Component {...props} />
    </Suspense>
);

// Layouts
import { LandingLayout } from '@/components/layout/LandingLayout'; // Keep Landing Layout eager for LCP
// Lazy load other layouts
const AuthLayout = Loadable(lazy(() => import('@/components/layout/AuthLayout').then(module => ({ default: module.AuthLayout }))));
const StudentLayout = Loadable(lazy(() => import('@/components/layout/StudentLayout').then(module => ({ default: module.StudentLayout }))));
const InstructorLayout = Loadable(lazy(() => import('@/components/layout/InstructorLayout').then(module => ({ default: module.InstructorLayout }))));
const AdminLayout = Loadable(lazy(() => import('@/components/layout/AdminLayout').then(module => ({ default: module.AdminLayout }))));
const DashboardSwitcher = Loadable(lazy(() => import('@/components/layout/DashboardSwitcher').then(module => ({ default: module.DashboardSwitcher }))));

// Protected Route
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Public Pages
import { LandingPage } from '@/pages/public/LandingPage'; // Keep Landing eager for LCP
// Lazy load other public pages
const CourseDetails = Loadable(lazy(() => import('@/pages/public/CourseDetails').then(module => ({ default: module.CourseDetails }))));
const InstructorPublicProfile = Loadable(lazy(() => import('@/pages/public/InstructorPublicProfile').then(module => ({ default: module.InstructorPublicProfile }))));

// Auth Pages
const Login = Loadable(lazy(() => import('@/pages/auth/Login').then(module => ({ default: module.Login }))));
const ForgotPassword = Loadable(lazy(() => import('@/pages/auth/ForgotPassword').then(module => ({ default: module.ForgotPassword }))));
const ResetPassword = Loadable(lazy(() => import('@/pages/auth/ResetPassword').then(module => ({ default: module.ResetPassword }))));
const Auth = Loadable(lazy(() => import('@/pages/auth/Auth').then(module => ({ default: module.default }))));

// Student Pages
const MyCourses = Loadable(lazy(() => import('@/pages/student/MyCourses').then(module => ({ default: module.MyCourses }))));
const CoursePlayer = Loadable(lazy(() => import('@/pages/student/CoursePlayer').then(module => ({ default: module.CoursePlayer }))));
const MyCertificates = Loadable(lazy(() => import('@/features/student/pages/MyCertificates').then(module => ({ default: module.MyCertificates }))));
const MyPayments = Loadable(lazy(() => import('@/features/payments/pages/MyPayments').then(module => ({ default: module.MyPayments }))));
const TestTaking = Loadable(lazy(() => import('@/features/tests/components/TestTaking').then(module => ({ default: module.TestTaking }))));
const Checkout = Loadable(lazy(() => import('@/features/payments/pages/Checkout').then(module => ({ default: module.Checkout }))));
const PaymentSuccess = Loadable(lazy(() => import('@/features/payments/pages/PaymentSuccess').then(module => ({ default: module.PaymentSuccess }))));
const PaymentFailure = Loadable(lazy(() => import('@/features/payments/pages/PaymentFailure').then(module => ({ default: module.PaymentFailure }))));
const PaymentCallback = Loadable(lazy(() => import('@/features/payments/pages/PaymentCallback').then(module => ({ default: module.PaymentCallback }))));
const MySessions = Loadable(lazy(() => import('@/pages/student/MySessions').then(module => ({ default: module.default }))));
const RequestMeeting = Loadable(lazy(() => import('@/pages/student/RequestMeeting').then(module => ({ default: module.default }))));

// Instructor Pages
const InstructorCourses = Loadable(lazy(() => import('@/pages/instructor/MyCourses').then(module => ({ default: module.InstructorCourses }))));
const CreateCourse = Loadable(lazy(() => import('@/pages/instructor/CreateCourse').then(module => ({ default: module.CreateCourse }))));
const EditCourse = Loadable(lazy(() => import('@/pages/instructor/EditCourse').then(module => ({ default: module.EditCourse }))));
const InstructorStudents = Loadable(lazy(() => import('@/pages/instructor/Students').then(module => ({ default: module.InstructorStudents }))));
const PendingGradings = Loadable(lazy(() => import('@/pages/instructor/PendingGradings').then(module => ({ default: module.PendingGradings }))));
const InstructorRevenue = Loadable(lazy(() => import('@/pages/instructor/Revenue').then(module => ({ default: module.InstructorRevenue }))));
const TestBuilderPage = Loadable(lazy(() => import('@/pages/instructor/TestBuilderPage').then(module => ({ default: module.TestBuilderPage }))));
const CourseStudentsPage = Loadable(lazy(() => import('@/pages/instructor/CourseStudentsPage').then(module => ({ default: module.CourseStudentsPage }))));
const InstructorKycPage = Loadable(lazy(() => import('@/features/instructor/pages/InstructorKycPage').then(module => ({ default: module.InstructorKycPage }))));
const ProfileEditor = Loadable(lazy(() => import('@/features/instructor/pages/ProfileEditor').then(module => ({ default: module.default }))));
const SessionsManagement = Loadable(lazy(() => import('@/pages/instructor/SessionsManagement').then(module => ({ default: module.default }))));
const GroupsManagement = Loadable(lazy(() => import('@/pages/instructor/GroupsManagement').then(module => ({ default: module.default }))));
const MeetingRequests = Loadable(lazy(() => import('@/pages/instructor/MeetingRequests').then(module => ({ default: module.default }))));

// Admin Pages
const AdminUsers = Loadable(lazy(() => import('@/pages/admin/Users').then(module => ({ default: module.AdminUsers }))));
const AdminCourses = Loadable(lazy(() => import('@/pages/admin/Courses').then(module => ({ default: module.AdminCourses }))));
const AdminPayments = Loadable(lazy(() => import('@/pages/admin/Payments').then(module => ({ default: module.AdminPayments }))));
const RolesPermissions = Loadable(lazy(() => import('@/pages/admin/RolesPermissions').then(module => ({ default: module.RolesPermissions }))));
const Settings = Loadable(lazy(() => import('@/pages/admin/Settings').then(module => ({ default: module.default }))));
const SubscriptionPlans = Loadable(lazy(() => import('@/pages/admin/SubscriptionPlans').then(module => ({ default: module.default }))));
const AdminKycManagementPage = Loadable(lazy(() => import('@/features/admin/pages/AdminKycManagementPage').then(module => ({ default: module.AdminKycManagementPage }))));
const LegalPagesList = Loadable(lazy(() => import('@/features/admin/pages/LegalPagesList').then(module => ({ default: module.LegalPagesList }))));
const LegalPageEditor = Loadable(lazy(() => import('@/features/admin/pages/LegalPageEditor').then(module => ({ default: module.LegalPageEditor }))));
const AdminPacks = Loadable(lazy(() => import('@/pages/admin/Packs').then(module => ({ default: module.AdminPacks }))));
const MediaProvidersManagementPage = Loadable(lazy(() => import('@/features/admin/pages/MediaProvidersManagementPage').then(module => ({ default: module.MediaProvidersManagementPage }))));

// Email Pages
const EmailTemplatesPage = Loadable(lazy(() => import('@/app/dashboard/email/templates').then(module => ({ default: module.default }))));
const EmailCampaignsPage = Loadable(lazy(() => import('@/app/dashboard/email/campaigns').then(module => ({ default: module.default }))));
const EmailLogTable = Loadable(lazy(() => import('@/features/email/components/EmailLogTable').then(module => ({ default: module.EmailLogTable }))));
const ProviderSettings = Loadable(lazy(() => import('@/features/email/components/ProviderSettings').then(module => ({ default: module.ProviderSettings }))));

// Pack Pages
const PackCatalog = Loadable(lazy(() => import('@/features/packs/pages/PackCatalog').then(module => ({ default: module.PackCatalog }))));
const PackDetails = Loadable(lazy(() => import('@/features/packs/pages/PackDetails').then(module => ({ default: module.PackDetails }))));
const MyPacks = Loadable(lazy(() => import('@/features/packs/pages/MyPacks').then(module => ({ default: module.MyPacks }))));
const InstructorPacks = Loadable(lazy(() => import('@/features/packs/pages/InstructorPacks').then(module => ({ default: module.InstructorPacks }))));

// Error Pages
const NotFound = Loadable(lazy(() => import('@/pages/errors/NotFound').then(module => ({ default: module.NotFound }))));
const Unauthorized = Loadable(lazy(() => import('@/pages/errors/Unauthorized').then(module => ({ default: module.Unauthorized }))));

// Session Video Rooms
const WaitingRoom = Loadable(lazy(() => import('@/pages/sessions/WaitingRoom').then(module => ({ default: module.default }))));
const LiveKitRoom = Loadable(lazy(() => import('@/pages/sessions/LiveKitRoom').then(module => ({ default: module.default }))));

// Public (Lazy)
const CourseCatalog = Loadable(lazy(() => import('@/pages/public/CourseCatalog').then(module => ({ default: module.default }))));
const Pricing = Loadable(lazy(() => import('@/pages/public/Pricing').then(module => ({ default: module.default }))));
const FAQ = Loadable(lazy(() => import('@/pages/public/FAQ').then(module => ({ default: module.default }))));
const Contact = Loadable(lazy(() => import('@/pages/public/Contact').then(module => ({ default: module.default }))));
const Privacy = Loadable(lazy(() => import('@/pages/public/Privacy').then(module => ({ default: module.default }))));
const Terms = Loadable(lazy(() => import('@/pages/public/Terms').then(module => ({ default: module.default }))));


// Helper component for role-based rendering on same route
import { useAuthStore } from '@/store/authStore';

const RoleBasedComponent = ({ student, instructor, admin }: { student?: React.ReactNode, instructor?: React.ReactNode, admin?: React.ReactNode }) => {
    const { user } = useAuthStore();

    if (!user) return null;

    const hasRole = (roleSlug: string) => user.roles.some(r => r.slug === roleSlug);


    if ((hasRole('admin') || hasRole('super_admin')) && admin) return <>{admin}</>;
    if (hasRole('instructor') && instructor) return <>{instructor}</>;
    if (hasRole('student') && student) return <>{student}</>;

    return <Navigate to={ROUTES.UNAUTHORIZED} />;
};

export const router = createBrowserRouter([
    // Public routes
    {
        path: '/',
        element: <LandingLayout />,
        children: [
            {
                index: true,
                element: <LandingPage />,
            },
            {
                path: ROUTES.COURSES,
                element: <CourseCatalog />,
            },
            {
                path: ROUTES.COURSE_DETAILS,
                element: <CourseDetails />,
            },
            {
                path: ROUTES.PRICING,
                element: <Pricing />
            },
            {
                path: ROUTES.FAQ,
                element: <FAQ />
            },
            {
                path: ROUTES.CONTACT,
                element: <Contact />
            },
            {
                path: ROUTES.TERMS,
                element: <Terms />
            },
            {
                path: ROUTES.PRIVACY,
                element: <Privacy />
            },
            {
                path: ROUTES.PACKS,
                element: <PackCatalog />,
            },
            {
                path: ROUTES.PACK_DETAILS,
                element: <PackDetails />,
            },
            {
                path: ROUTES.PUBLIC_INSTRUCTOR_PROFILE,
                element: <InstructorPublicProfile />,
            },
        ],
    },

    // Auth routes
    {
        path: '/',
        element: <AuthLayout />,
        children: [
            {
                path: ROUTES.LOGIN,
                element: <Login />,
            },
            {
                path: ROUTES.REGISTER,
                element: <Auth />,
            },
            {
                path: ROUTES.FORGOT_PASSWORD,
                element: <ForgotPassword />,
            },
            {
                path: ROUTES.RESET_PASSWORD,
                element: <ResetPassword />,
            },
        ],
    },

    // Unified Dashboard Route
    {
        path: ROUTES.DASHBOARD,
        element: (
            <ProtectedRoute>
                <DashboardSwitcher />
            </ProtectedRoute>
        ),
    },

    // Student Routes (Unified paths but protected by role)
    {
        path: '/',
        element: (
            <ProtectedRoute allowedRoles={['student']}>
                <StudentLayout />
            </ProtectedRoute>
        ),
        children: [
            // {
            //     path: ROUTES.MY_COURSES,
            //     element: <MyCourses />,
            // },
            {
                path: ROUTES.STUDENT_COURSE_PLAYER,
                element: <CoursePlayer />,
            },
            {
                path: ROUTES.MY_PACKS,
                element: <MyPacks />,
            },
            {
                path: ROUTES.STUDENT_CERTIFICATES,
                element: <MyCertificates />,
            },
            {
                path: ROUTES.STUDENT_PAYMENTS,
                element: <MyPayments />,
            },
            {
                path: 'tests/:testId/take',
                element: <TestTaking />,
            },
            {
                path: ROUTES.MY_SESSIONS,
                element: <MySessions />,
            },
            {
                path: ROUTES.REQUEST_MEETING,
                element: <RequestMeeting />,
            },
        ],
    },

    // Checkout Route (Protected for Student)
    {
        path: ROUTES.CHECKOUT,
        element: (
            <ProtectedRoute allowedRoles={['student']}>
                <Checkout />
            </ProtectedRoute>
        ),
    },

    // Payment Routes (Public - no auth required for callbacks)
    {
        path: ROUTES.PAYMENT_CALLBACK,
        element: <PaymentCallback />,
    },
    {
        path: ROUTES.PAYMENT_SUCCESS,
        element: <PaymentSuccess />,
    },
    {
        path: ROUTES.PAYMENT_FAILURE,
        element: <PaymentFailure />,
    },

    // Instructor Routes
    {
        path: '/',
        element: (
            <ProtectedRoute allowedRoles={['instructor']}>
                <InstructorLayout />
            </ProtectedRoute>
        ),
        children: [
            {
                path: ROUTES.INSTRUCTOR_CREATE_COURSE,
                element: <CreateCourse />,
            },
            {
                path: ROUTES.INSTRUCTOR_EDIT_COURSE,
                element: <EditCourse />,
            },
            {
                path: ROUTES.INSTRUCTOR_PACKS,
                element: <InstructorPacks />,
            },
            {
                path: ROUTES.INSTRUCTOR_STUDENTS,
                element: <InstructorStudents />,
            },
            {
                path: ROUTES.INSTRUCTOR_PENDING_GRADINGS,
                element: <PendingGradings />,
            },
            {
                path: ROUTES.INSTRUCTOR_REVENUE,
                element: <InstructorRevenue />,
            },
            {
                path: ROUTES.SESSIONS,
                element: <SessionsManagement />,
            },
            {
                path: ROUTES.GROUPS,
                element: <GroupsManagement />,
            },
            {
                path: ROUTES.MEETING_REQUESTS,
                element: <MeetingRequests />,
            },
            {
                path: ROUTES.INSTRUCTOR_TEST_BUILDER,
                element: <TestBuilderPage />,
            },
            {
                path: ROUTES.INSTRUCTOR_TEST_EDIT,
                element: <TestBuilderPage />,
            },
            {
                path: ROUTES.INSTRUCTOR_COURSE_STUDENTS,
                element: <CourseStudentsPage />,
            },
            {
                path: ROUTES.INSTRUCTOR_KYC,
                element: <InstructorKycPage />,
            },
            {
                path: ROUTES.INSTRUCTOR_PUBLIC_PROFILE_SETTINGS,
                element: <ProfileEditor />,
            },
            {
                path: ROUTES.INSTRUCTOR_VIDEOS,
                async lazy() {
                    const { default: VideosPage } = await import('@/pages/instructor/VideosPage');
                    return { Component: VideosPage };
                },
            },
        ],
    },

    // Shared Route for My Courses (if we want to unify the URL)
    {
        path: ROUTES.MY_COURSES,
        element: (
            <ProtectedRoute>
                {/* We need a switcher here too if we want the same URL */}
                <RoleBasedComponent
                    student={<StudentLayout><MyCourses /></StudentLayout>}
                    instructor={<InstructorLayout><InstructorCourses /></InstructorLayout>}
                />
            </ProtectedRoute>
        )
    },

    // Admin Routes
    {
        path: '/',
        element: (
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminLayout />
            </ProtectedRoute>
        ),
        children: [
            {
                path: ROUTES.ADMIN_USERS,
                element: <AdminUsers />,
            },
            // {
            //     path: ROUTES.ADMIN_USERS,
            //     element: <UserPermissions />,
            // },
            {
                path: ROUTES.ADMIN_COURSES,
                element: <AdminCourses />,
            },
            {
                path: ROUTES.ADMIN_PACKS,
                element: <AdminPacks />,
            },
            {
                path: ROUTES.ADMIN_PAYMENTS,
                element: <AdminPayments />,
            },
            {
                path: ROUTES.ADMIN_ROLES,
                element: <RolesPermissions />,
            },
            {
                path: ROUTES.ADMIN_SUBSCRIPTION_PLANS,
                element: <SubscriptionPlans />,
            },
            {
                path: ROUTES.ADMIN_EDIT_COURSE,
                element: <EditCourse />,
            },
            {
                path: ROUTES.ADMIN_EDIT_PACK,
                element: <InstructorPacks />,
            },
            {
                path: '/dashboard/email/templates',
                element: <EmailTemplatesPage />,
            },
            {
                path: '/dashboard/email/campaigns',
                element: <EmailCampaignsPage />,
            },
            {
                path: '/dashboard/email/logs',
                element: <div className="container mx-auto py-6 px-4"><EmailLogTable /></div>,
            },
            {
                path: '/dashboard/email/settings',
                element: <div className="container mx-auto py-6 px-4"><ProviderSettings /></div>,
            },
            {
                path: ROUTES.ADMIN_KYC,
                element: <AdminKycManagementPage />,
            },
            {
                path: ROUTES.ADMIN_LEGAL_LIST,
                element: <LegalPagesList />,
            },
            {
                path: ROUTES.ADMIN_LEGAL_EDIT,
                element: <LegalPageEditor />,
            },
            {
                path: ROUTES.ADMIN_MEDIA_PROVIDERS,
                element: <MediaProvidersManagementPage />,
            },
            // Settings is now handled by the shared route
        ],
    },

    // User Settings Route (Protected for all authenticated users)
    {
        path: ROUTES.SETTINGS,
        element: (
            <ProtectedRoute>
                <RoleBasedComponent
                    student={<StudentLayout><Settings /></StudentLayout>}
                    instructor={<InstructorLayout><Settings /></InstructorLayout>}
                    admin={<AdminLayout><Settings /></AdminLayout>}
                />
            </ProtectedRoute>
        ),
    },

    // Session Room Routes (Protected but not layout-specific)
    {
        path: ROUTES.SESSION_WAITING_ROOM,
        element: (
            <ProtectedRoute>
                <WaitingRoom />
            </ProtectedRoute>
        ),
    },
    {
        path: ROUTES.SESSION_ROOM,
        element: (
            <ProtectedRoute>
                <LiveKitRoom />
            </ProtectedRoute>
        ),
    },

    // Error routes
    {
        path: ROUTES.UNAUTHORIZED,
        element: <Unauthorized />,
    },
    {
        path: '*',
        element: <NotFound />,
    },
]);
